import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { mlSheetService } from "../../services/mlSheetService"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader"
import Alert from "../common/Alert"
import { Plus, ArrowLeft, Package, ChevronLeft, ChevronRight, XCircle, AlertTriangle, CheckCircle2 } from "lucide-react";
import Button from '../common/Button.js';
import { groupAccessAgents, formatAccessAgentMeta, matchingAccessAgentIds } from '../../utils/mlSheetAccessAgentList.js';
import CreateVehicleWithOwnerModal from './CreateVehicleWithOwnerModal.js';
import MLSheetShipmentRecordList from './MLSheetShipmentRecordList.js';
import MlAccessAgentChips from './MlAccessAgentChips.js';
import MlLocationChips from './MlLocationChips.js';
import { mergeCanAccess } from '../../utils/companyFeatures.js';
import { hasInternalHubDispatchControls, hasShipmentCrossingAccess, isMlAccessUsersRequired, shouldDispatchIncomingOgclOnly } from '../../utils/networkMode.js';
import { toLocationsForMlDispatch } from '../../utils/hubDispatchFilter.js';
import vehicleOwnerService from '../../services/VehicleOwnerService.js';
import { enrichVehiclesWithOwners, formatVehicleLabel } from '../../utils/vehicleOwnerDisplay.js';
import { formatHubBranchLabel } from '../../utils/destinationType.js';
import {
  formatLocationDropdownLabel,
  filterLocationsByQuery,
} from '../../utils/locationDropdownLabel.js';
import { isLrShipmentFormat } from '../../utils/shipmentIdFormat.js';
import companySettingsService from '../../services/companySettingsService.js';
import { showMlSheetAutoSyncAlerts } from '../../utils/mlSheetAutoSyncAlerts.js';
import {
  ML_MOBILE_DROPDOWN_PANEL,
  ML_MOBILE_DROPDOWN_SEARCH,
  ML_MOBILE_DROPDOWN_ITEM,
  ML_MOBILE_DROPDOWN_ITEM_TEXT,
  ML_MOBILE_FIELD_LABEL,
  ML_MOBILE_TRIGGER_TEXT,
  mlMobileTriggerClass,
} from '../../utils/mlSheetMobileDropdown.js';
import { useTranslation } from 'react-i18next';
import useMlCreationPaidSettlement from '../../hooks/useMlCreationPaidSettlement.js';
import { getUnremittedPaidShipments, isShipmentDisabledForMl } from '../../utils/mlPaidSettlementRules.js';
import { useRequirePaidSettlementBeforeMl } from '../../hooks/useRequirePaidSettlementBeforeMl.js';
import MlPaidSettlementGate from './MlPaidSettlementGate.js';
import { evaluateVehicleLoad, formatKg } from '../../utils/vehicleLoad.js';
import routeService from '../../services/RouteService.js';
import { formatRouteLabel, routeDestinationSequence } from '../../utils/routeDisplay.js';
import MlAssignToLocationBlock from './MlAssignToLocationBlock.js';
import { hubIdOf, ownHubsEligibleForMlLocationAssign } from '../../utils/mlAssignToLocation.js';

const MOBILE_STEPS = [
  { id: "filters", label: "Filters" },
  { id: "vehicle", label: "Vehicle" },
  { id: "shipments", label: "Shipments" },
]


const AddMLSheet = () => {
  const { t } = useTranslation();
  const [openDropdown, setOpenDropdown] = useState(null)
  const [searchInputs, setSearchInputs] = useState({
    from: "",
    to: "",
    crossing: "",
    vehicle: "",
    access: "",
    route: "",
  })
  const fromRef = useRef(null)
  const toRef = useRef(null)
  const crossingRef = useRef(null)
  const vehicleRef = useRef(null)
  const accessRef = useRef(null)
  const routeRef = useRef(null)
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [searchingShipments, setSearchingShipments] = useState(false)
  const [agents, setAgents] = useState([])
  const [vehicles, setVehicles] = useState([])
  const [locations, setLocations] = useState([])
  const [useLrLocationLabels, setUseLrLocationLabels] = useState(false)
  const [routes, setRoutes] = useState([])
  // true when locations are locked to an explicitly-selected route
  const [routeLocked, setRouteLocked] = useState(false)
  const [filteredShipments, setFilteredShipments] = useState([])
  const [selectedShipmentIds, setSelectedShipmentIds] = useState([])
  const [selectAllChecked, setSelectAllChecked] = useState(false)
  const shipmentSearchTokenRef = useRef(0)
  const shipmentSearchDebounceRef = useRef(null)

  const [formData, setFormData] = useState({
    from_location_ids: [],
    to_location_ids: [],
    crossing_location_hub_ids: [],
    from_date: "",
    to_date: "",
    vehicle_id: "",
    driver_contact: "",
    access_user_ids: [],
    route_id: "",
  })
  const [assignToLocation, setAssignToLocation] = useState(false)
  const [assignedLocationHubId, setAssignedLocationHubId] = useState("")

  // Vehicle creation modal
  const [showVehicleModal, setShowVehicleModal] = useState(false)
  const [pendingVehicleSearch, setPendingVehicleSearch] = useState('')

  const companyAccess = mergeCanAccess(user?.company_features || user?.company_can_access || {})
  const accessUsersRequired = isMlAccessUsersRequired(user)
  const canSelectCrossingHubs = hasShipmentCrossingAccess(user)
  const fromLocationLocked = ['Agent', 'Hub Admin'].includes(user?.role || user?.role_name)
  const canAddVehicle = (user?.role || user?.role_name) === 'Admin'
  const incomingOgclOnly = shouldDispatchIncomingOgclOnly(user)

  const [mobileStep, setMobileStep] = useState(0)
  const {
    mlCreationBlock,
    dues,
    agentDues,
    isHubAdmin,
    hardBlocked: mlHardBlocked,
    showGate: showPaidSettlementGate,
    settlementPath,
    settleOpen,
    setSettleOpen,
    refresh: refreshMlSettlement,
  } = useMlCreationPaidSettlement(user)

  const requiresPaidBeforeMl = useRequirePaidSettlementBeforeMl(user)
  const shipmentDisabledForMl = (shipment) => isShipmentDisabledForMl(shipment, user, requiresPaidBeforeMl)

  const selectedPaidBlocked = useMemo(
    () => (requiresPaidBeforeMl
      ? getUnremittedPaidShipments(filteredShipments, selectedShipmentIds)
      : []),
    [requiresPaidBeforeMl, filteredShipments, selectedShipmentIds],
  )
  const submitBlocked = mlHardBlocked || selectedPaidBlocked.length > 0
  const [alreadyLoadedKg, setAlreadyLoadedKg] = useState(0)
  const mobileStepRef = useRef(0)
  const [isMobileLayout, setIsMobileLayout] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  )

  useEffect(() => {
    mobileStepRef.current = mobileStep
  }, [mobileStep])

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)")
    const handler = (e) => setIsMobileLayout(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])

  const mobileStepPanelClass = (step) => {
    if (!isMobileLayout) return ""
    if (mobileStep !== step) return "hidden"
    return step === MOBILE_STEPS.length - 1
      ? "flex flex-col flex-1 min-h-0 overflow-hidden pb-4"
      : "flex flex-col flex-1 min-h-0 overflow-y-auto"
  }

  const validateMobileStep = (step) => {
    if (step === 0) {
      if (formData.from_location_ids.length === 0) {
        Alert.error("Please select at least one from location")
        return false
      }
      if (formData.to_location_ids.length === 0) {
        Alert.error("Please select at least one to location")
        return false
      }
      return true
    }
    if (step === 1) {
      if (!formData.vehicle_id) {
        Alert.error("Please select a vehicle")
        return false
      }
      if (accessUsersRequired && !assignToLocation && formData.access_user_ids.length === 0) {
        Alert.error("Please select at least one access user")
        return false
      }
      return true
    }
    return true
  }

  const goToNextMobileStep = () => {
    const step = mobileStepRef.current
    if (!validateMobileStep(step)) return
    const nextStep = Math.min(step + 1, MOBILE_STEPS.length - 1)
    mobileStepRef.current = nextStep
    setMobileStep(nextStep)
  }

  const goToPrevMobileStep = () => {
    setMobileStep((s) => {
      const prev = Math.max(s - 1, 0)
      mobileStepRef.current = prev
      return prev
    })
  }

  const handleMobileStepTabClick = (idx) => {
    const current = mobileStepRef.current
    if (idx === current) return
    if (idx < current) {
      mobileStepRef.current = idx
      setMobileStep(idx)
      return
    }
    for (let step = current; step < idx; step += 1) {
      if (!validateMobileStep(step)) return
    }
    mobileStepRef.current = idx
    setMobileStep(idx)
  }

  const handleMobilePrimaryAction = () => {
    if (mobileStepRef.current < MOBILE_STEPS.length - 1) {
      goToNextMobileStep()
      return
    }
    handleSubmit({ preventDefault: () => {} })
  }

  useEffect(() => {
    fetchInitialData()
  }, [])

  // Same-day load already on this vehicle (other ML sheets)
  useEffect(() => {
    let cancelled = false
    const vehicleId = formData.vehicle_id
    if (!vehicleId || !user?.is_cargo_shipment) {
      setAlreadyLoadedKg(0)
      return undefined
    }
    ;(async () => {
      try {
        const res = await mlSheetService.getVehicleDayCapacity({ vehicleId })
        const kg = res?.data?.data?.already_loaded_kg
        if (!cancelled) setAlreadyLoadedKg(Number(kg) || 0)
      } catch {
        if (!cancelled) setAlreadyLoadedKg(0)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [formData.vehicle_id, user?.is_cargo_shipment])

  useEffect(() => {
    if (filteredShipments.length === 0) {
      setSelectAllChecked(false)
      return
    }
    setSelectAllChecked(
      filteredShipments.every((s) => selectedShipmentIds.includes(s.shipment_id || s.id)),
    )
  }, [selectedShipmentIds, filteredShipments])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !fromRef.current?.contains(e.target) &&
        !toRef.current?.contains(e.target) &&
        !crossingRef.current?.contains(e.target) &&
        !vehicleRef.current?.contains(e.target) &&
        !accessRef.current?.contains(e.target) &&
        !routeRef.current?.contains(e.target)
      ) {
        setOpenDropdown(null)
        // Clear search inputs when dropdown closes
        setSearchInputs({
          from: "",
          to: "",
          crossing: "",
          vehicle: "",
          access: "",
          route: "",
        })
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Dynamic shipment search when filters change
  // Dynamic shipment search when filters change — debounced so rapid
  // checkbox toggling (From, then To, then From again) settles on ONE
  // request with the final filter state, instead of racing multiple
  // in-flight requests where a stale/broader one can overwrite the
  // correct, narrower result.
  useEffect(() => {
    if (shipmentSearchDebounceRef.current) {
      clearTimeout(shipmentSearchDebounceRef.current)
    }
    shipmentSearchDebounceRef.current = setTimeout(() => {
      searchShipments()
    }, 350)
    return () => {
      if (shipmentSearchDebounceRef.current) {
        clearTimeout(shipmentSearchDebounceRef.current)
      }
    }
  }, [formData.from_location_ids, formData.to_location_ids, formData.from_date, formData.to_date])

  // In custom (unlocked) mode, auto-detect the best matching route from the
  // selected from/to locations and preselect it so it gets saved on the sheet.
  useEffect(() => {
    if (routeLocked) return
    const from = formData.from_location_ids
    const to = formData.to_location_ids
    if (from.length === 0 || to.length === 0) {
      setFormData((prev) => (prev.route_id ? { ...prev, route_id: "" } : prev))
      return
    }
    let cancelled = false
    routeService
      .match(from, to)
      .then((res) => {
        if (cancelled) return
        const matched = res?.data
        setFormData((prev) => ({ ...prev, route_id: matched?.id || "" }))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [formData.from_location_ids, formData.to_location_ids, routeLocked])

  const fetchInitialData = async () => {
    try {
      setLoading(true)
      const [agentsRes, vehiclesRes, locationsRes, ownersRes, routesRes, settingsRes] = await Promise.all([
        mlSheetService.getAgents(),
        mlSheetService.getAllVehiclesNoPage(),
        mlSheetService.getLocations(),
        vehicleOwnerService.getAll(1, 500).catch(() => ({ data: [] })),
        routeService.getAll(1, 1000).catch(() => ({ data: [] })),
        companySettingsService.getSettings().catch(() => ({ data: {} })),
      ])

      console.log("[Dynamic] Agents Response:", agentsRes.data)
      console.log("[Dynamic] Vehicles Response:", vehiclesRes.data)
      console.log("[Dynamic] Locations Response:", locationsRes.data)

      const agentsData = agentsRes.data.data || agentsRes.data.users || agentsRes.data || []
      const vehiclesData = vehiclesRes.data.data || vehiclesRes.data.vehicles || vehiclesRes.data || []
      const ownersData = ownersRes?.data || ownersRes || []
      const locationsData = locationsRes.data.hubs || locationsRes.data.data || locationsRes.data || []

      console.log("[Dynamic] Extracted Agents:", agentsData)
      console.log("[Dynamic] Extracted Vehicles:", vehiclesData)
      console.log("[Dynamic] Extracted Locations:", locationsData)

      setAgents(Array.isArray(agentsData) ? agentsData : [])
      setVehicles(
        enrichVehiclesWithOwners(
          Array.isArray(vehiclesData) ? vehiclesData : [],
          Array.isArray(ownersData) ? ownersData : []
        )
      )
      setLocations(Array.isArray(locationsData) ? locationsData : [])
      setUseLrLocationLabels(
        !!settingsRes?.data?.lr_branch_code_required
        || isLrShipmentFormat(settingsRes?.data?.shipment_id_format),
      )

      const routesData = routesRes?.data?.data || routesRes?.data || []
      setRoutes(Array.isArray(routesData) ? routesData : [])

      // Auto-select user's assigned hub/branch as "From Location"
      if (Array.isArray(locationsData) && (user?.location_hub_id || user?.city)) {
        if (fromLocationLocked && user?.location_hub_id) {
          setFormData((prev) => ({
            ...prev,
            from_location_ids: [user.location_hub_id],
          }))
        } else {
          const userLocationObj = locationsData.find(
            (loc) =>
              String(loc.id) === String(user.location_hub_id) ||
              formatHubBranchLabel(loc) === user.city ||
              (loc.location || loc.hub_name || loc.name) === user.city
          )
          if (userLocationObj) {
            const locId = userLocationObj.id || userLocationObj.hub_id
            setFormData((prev) => ({
              ...prev,
              from_location_ids: [locId],
            }))
          }
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error)
      Alert.error("Failed to load initial data")
    } finally {
      setLoading(false)
    }
  }

  const searchShipments = async () => {
    const requestToken = ++shipmentSearchTokenRef.current
    // Only search if at least one filter is set
    if (formData.from_location_ids.length === 0 && formData.to_location_ids.length === 0 && !formData.from_date && !formData.to_date) {
      setFilteredShipments([])
      setSelectedShipmentIds([])
      setSelectAllChecked(false)
      return
    }

    try {
      setSearchingShipments(true)

      const searchParams = {}
      const findLocById = (id) =>
        locations.find((l) => String(l.id || l.hub_id) === String(id))

      // Prefer hub/branch IDs so hub↔branch routes match correctly
      if (formData.from_location_ids.length > 0) {
        searchParams.from_location_ids = formData.from_location_ids.join(",")
        const fromLabels = formData.from_location_ids
          .map((id) => {
            const loc = findLocById(id)
            return loc ? locationOptionLabel(loc) : null
          })
          .filter(Boolean)
        if (fromLabels.length > 0) {
          searchParams.from_location = fromLabels.join(",")
        }
      }

      if (formData.to_location_ids.length > 0) {
        searchParams.to_location_ids = formData.to_location_ids.join(",")
        const toLabels = formData.to_location_ids
          .map((id) => {
            const loc = findLocById(id)
            return loc ? locationOptionLabel(loc) : null
          })
          .filter(Boolean)
        if (toLabels.length > 0) {
          searchParams.to_location = toLabels.join(",")
        }
      }

      if (formData.from_date) searchParams.from_date = formData.from_date
      if (formData.to_date) searchParams.to_date = formData.to_date
      if (incomingOgclOnly) searchParams.incoming_ogcl_only = true

      const response = await mlSheetService.getBookedShipments(searchParams)

      // A newer search has started since this request was sent — ignore
      // this now-stale response so it can't clobber the latest filters.
      if (requestToken !== shipmentSearchTokenRef.current) return

      console.log("[Dynamic] Shipments Response:", response.data)

      const shipmentsData = response.data.shipments || response.data.data || response.data || []

      console.log("[Dynamic] Extracted Shipments:", shipmentsData)

      const list = Array.isArray(shipmentsData) ? shipmentsData : []
      setFilteredShipments(list)

      // To location also auto-selects dest agents — select every selectable GC at the same time
      if (formData.to_location_ids.length > 0) {
        setSelectedShipmentIds(
          list
            .filter((s) => !shipmentDisabledForMl(s))
            .map((s) => s.shipment_id || s.id)
            .filter(Boolean),
        )
      } else {
        setSelectedShipmentIds([])
        setSelectAllChecked(false)
      }
    } catch (error) {
      if (requestToken !== shipmentSearchTokenRef.current) return
      console.error("Error searching shipments:", error)
      setFilteredShipments([])
      Alert.error(error.response?.data?.error || "Failed to load shipments for dispatch")
    } finally {
      if (requestToken === shipmentSearchTokenRef.current) {
        setSearchingShipments(false)
      }
    }
  }

  const handleLocationToggle = (locationType, locationId) => {
    if (fromLocationLocked && locationType === "from") return
    setFormData((prev) => {
      const key = locationType === "from" ? "from_location_ids" : "to_location_ids"
      const currentIds = prev[key]
      const idStr = String(locationId)
      const alreadySelected = currentIds.some((id) => String(id) === idStr)

      let nextFrom = prev.from_location_ids
      let nextTo = prev.to_location_ids

      if (locationType === "from") {
        nextFrom = alreadySelected
          ? currentIds.filter((id) => String(id) !== idStr)
          : [...currentIds, locationId]
        // Drop To locations that are no longer eligible (dispatch + destination mapping)
        const eligibleToIds = new Set(
          toLocationsForMlDispatch(locations, nextFrom, {
            enabled: hasInternalHubDispatchControls(user),
          }).map((loc) => String(loc.id || loc.hub_id)),
        )
        nextTo = prev.to_location_ids.filter((id) => eligibleToIds.has(String(id)))
        return { ...prev, from_location_ids: nextFrom, to_location_ids: nextTo }
      }

      nextTo = alreadySelected
        ? currentIds.filter((id) => String(id) !== idStr)
        : [...currentIds, locationId]
      return { ...prev, to_location_ids: nextTo }
    })
  }

  const handleCrossingHubToggle = (locationId) => {
    setFormData((prev) => {
      const currentIds = prev.crossing_location_hub_ids || []
      const idStr = String(locationId)
      const alreadySelected = currentIds.some((id) => String(id) === idStr)
      return {
        ...prev,
        crossing_location_hub_ids: alreadySelected
          ? currentIds.filter((id) => String(id) !== idStr)
          : [...currentIds, locationId],
      }
    })
  }

  const isLocationSelected = (selectedIds, locationId) =>
    selectedIds.some((id) => String(id) === String(locationId))

  const handleDateChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  const handleSelectShipment = (shipmentId) => {
    const shipment = filteredShipments.find((s) => (s.shipment_id || s.id) === shipmentId)
    if (shipment && shipmentDisabledForMl(shipment)) {
      Alert.error('This Paid shipment has unremitted collections. Settle it first or select To Pay shipments only.')
      return
    }
    setSelectedShipmentIds((prev) =>
      prev.includes(shipmentId) ? prev.filter((id) => id !== shipmentId) : [...prev, shipmentId],
    )
  }

  const handleSelectAll = () => {
    const selectable = filteredShipments.filter((s) => !shipmentDisabledForMl(s))
    if (selectAllChecked) {
      setSelectedShipmentIds([])
    } else {
      setSelectedShipmentIds(selectable.map((s) => s.shipment_id || s.id))
    }
  }

  const handleRouteSelect = (e) => {
    const { value } = e.target
    if (!value) {
      // "Custom" — unlock manual location selection (backend still auto-matches)
      setRouteLocked(false)
      setFormData((prev) => ({ ...prev, route_id: "" }))
      return
    }
    const route = routes.find((r) => String(r.id) === String(value))
    if (!route) return
    const fromIds = route.from_location_hub_id != null ? [route.from_location_hub_id] : []
    let toIds = routeDestinationSequence(route)
    if (fromIds.length > 0) {
      const eligibleToIds = new Set(
        toLocationsForMlDispatch(locations, fromIds, {
          enabled: hasInternalHubDispatchControls(user),
        }).map((loc) => String(loc.id || loc.hub_id)),
      )
      toIds = toIds.filter((id) => eligibleToIds.has(String(id)))
    }
    setRouteLocked(true)
    setFormData((prev) => ({
      ...prev,
      route_id: route.id,
      from_location_ids: fromLocationLocked ? prev.from_location_ids : fromIds,
      to_location_ids: toIds,
    }))
  }

  const handleVehicleChange = (e) => {
    const { value } = e.target
    const selectedVehicle = vehicles.find((v) => v.id === value || v.vehicle_id === value || String(v.id) === String(value))

    setFormData({
      ...formData,
      vehicle_id: selectedVehicle?.id || selectedVehicle?.vehicle_id || value,
      driver_contact: selectedVehicle?.driver_contact || "",
    })
  }

  // Cargo overweight check: compare selected shipments' chargeable weight
  // against the chosen vehicle's capacity when the company is in cargo mode.
  const isCargoMode = !!user?.is_cargo_shipment
  const selectedVehicleObj = vehicles.find(
    (v) => (v.id || v.vehicle_id) === formData.vehicle_id
  )
  const selectedShipmentObjs = filteredShipments.filter((s) =>
    selectedShipmentIds.includes(s.shipment_id || s.id)
  )
  const load = evaluateVehicleLoad({
    vehicle: selectedVehicleObj,
    shipments: selectedShipmentObjs,
    alreadyLoadedKg,
  })
  const isOverweight = isCargoMode && load.hasCapacity && load.overweight

  const handleRemoveExtraShipments = () => {
    if (!load.extraShipmentIds.length) return
    setSelectedShipmentIds((prev) =>
      prev.filter((id) => !load.extraShipmentIds.includes(id))
    )
    setSelectAllChecked(false)
  }

  // FIX: Add handler for manual driver contact input
  const handleDriverContactChange = (e) => {
    const digits = String(e.target.value || "").replace(/\D/g, "").slice(0, 10)
    setFormData({
      ...formData,
      driver_contact: digits,
    })
  }

  const handleAccessUserChange = (userId) => {
    if (accessUsersRequired && userId === user?.user_id) {
      Alert.error('You cannot assign yourself as an ML access agent')
      return
    }
    setFormData((prev) => ({
      ...prev,
      access_user_ids: prev.access_user_ids.includes(userId)
        ? prev.access_user_ids.filter((id) => id !== userId)
        : [...prev.access_user_ids, userId],
    }))
  }

  const handleSearchInput = (dropdownType, value) => {
    setSearchInputs(prev => ({
      ...prev,
      [dropdownType]: value
    }))
  }

  const getFilteredLocations = (searchTerm) =>
    filterLocationsByQuery(locations, searchTerm)

  const toLocationsForDispatch = () =>
    toLocationsForMlDispatch(locations, formData.from_location_ids, {
      enabled: hasInternalHubDispatchControls(user),
    })

  const getFilteredToLocations = (searchTerm) =>
    filterLocationsByQuery(toLocationsForDispatch(), searchTerm)

  const locationOptionLabel = (loc) =>
    formatLocationDropdownLabel(loc, useLrLocationLabels)
      || loc.location
      || loc.hub_name
      || loc.name
      || ''

  const getFilteredVehicles = (searchTerm) => {
    return vehicles.filter(vehicle => {
      const vehicleNumber = (vehicle.vehicle_number || "").toLowerCase()
      const vehicleType = (vehicle.vehicle_type || "").toLowerCase()
      const ownerName = (vehicle.owner_name || "").toLowerCase()
      const searchLower = searchTerm.toLowerCase()
      return (
        vehicleNumber.includes(searchLower) ||
        vehicleType.includes(searchLower) ||
        ownerName.includes(searchLower)
      )
    })
  }

  const getFilteredRoutes = (searchTerm) => {
    const q = (searchTerm || "").toLowerCase().trim()
    if (!q) return routes
    return routes.filter((route) => formatRouteLabel(route).toLowerCase().includes(q))
  }

  const renderAccessAgentOption = (agent) => {
    const agentId = agent.user_id || agent.id
    const meta = formatAccessAgentMeta(agent)
    return (
      <label
        key={agentId}
        className={ML_MOBILE_DROPDOWN_ITEM}
      >
        <input
          type="checkbox"
          checked={formData.access_user_ids.includes(agentId)}
          onChange={() => handleAccessUserChange(agentId)}
          className="w-3.5 h-3.5 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0"
        />
        <div className="min-w-0">
          <p className={`${ML_MOBILE_DROPDOWN_ITEM_TEXT} font-semibold text-secondary`}>{agent.name}</p>
          {meta && <p className="text-[9px] sm:text-xs text-gray-500 mt-0.5 break-words">{meta}</p>}
        </div>
      </label>
    )
  }

  const accessAgentGroups = groupAccessAgents(
    agents,
    searchInputs.access,
    formData.to_location_ids,
    locations,
  )

  const locationAssignHubs = useMemo(
    () => ownHubsEligibleForMlLocationAssign(formData.to_location_ids, locations, agents),
    [formData.to_location_ids, locations, agents],
  )

  useEffect(() => {
    if (!locationAssignHubs.length) {
      setAssignToLocation(false)
      setAssignedLocationHubId("")
      return
    }
    setAssignToLocation(true)
    setAssignedLocationHubId((prev) => {
      const ids = locationAssignHubs.map((h) => String(hubIdOf(h)))
      if (prev && ids.includes(String(prev))) return prev
      return hubIdOf(locationAssignHubs[0])
    })
  }, [locationAssignHubs])

  useEffect(() => {
    if (assignToLocation) return
    const ids = matchingAccessAgentIds(
      agents,
      formData.to_location_ids,
      locations,
      user?.user_id,
    )
    setFormData((prev) => {
      const same =
        prev.access_user_ids.length === ids.length &&
        ids.every((id) => prev.access_user_ids.some((x) => String(x) === String(id)))
      if (same) return prev
      return { ...prev, access_user_ids: ids }
    })
  }, [assignToLocation, agents, formData.to_location_ids, locations, user?.user_id])

  const handleVehicleCreated = (createdVehicle) => {
    const newVehicleId = createdVehicle.id || createdVehicle.vehicle_id
    setVehicles(prev => [...prev, createdVehicle])
    setFormData(prev => ({
      ...prev,
      vehicle_id: newVehicleId,
      driver_contact: createdVehicle.driver_contact || prev.driver_contact,
    }))
    setOpenDropdown(null)
    setSearchInputs(prev => ({ ...prev, vehicle: '' }))
  }

  const openVehicleModal = (prefillNumber = '') => {
    setPendingVehicleSearch(prefillNumber || searchInputs.vehicle)
    setShowVehicleModal(true)
    setOpenDropdown(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (mlHardBlocked) {
      Alert.error(mlCreationBlock.reason || 'ML sheet creation is temporarily blocked')
      return
    }

    if (selectedPaidBlocked.length > 0) {
      Alert.error(
        `Cannot add Paid shipment(s) with unremitted collections: ${
          selectedPaidBlocked.map((s) => s.shipment_id).join(', ')
        }. Settle them or select To Pay shipments only.`,
      )
      return
    }

    if (isMobileLayout && mobileStepRef.current < MOBILE_STEPS.length - 1) {
      goToNextMobileStep()
      return
    }

    // Validation
    if (formData.from_location_ids.length === 0) {
      Alert.error("Please select at least one from location")
      return
    }

    if (formData.to_location_ids.length === 0) {
      Alert.error("Please select at least one to location")
      return
    }

    if (!formData.vehicle_id) {
      Alert.error("Please select a vehicle")
      return
    }

    const driverContact = String(formData.driver_contact || "").trim()
    if (driverContact && !/^\d{10}$/.test(driverContact)) {
      Alert.error("Driver contact must be a 10-digit mobile number")
      return
    }

    if (selectedShipmentIds.length === 0) {
      Alert.error("Please select at least one shipment")
      return
    }

    if (accessUsersRequired && !assignToLocation && formData.access_user_ids.length === 0) {
      Alert.error("Please select at least one access user")
      return
    }
    if (assignToLocation && !assignedLocationHubId) {
      Alert.error("Select the own hub to assign this ML sheet to")
      return
    }

    if (isOverweight) {
      Alert.error(
        `Vehicle will be overweight by ${formatKg(load.excessKg)} ` +
          `(new ${formatKg(load.newWeight)} + already loaded today ${formatKg(load.alreadyLoadedKg)} ` +
          `> capacity ${formatKg(load.capacityKg)}; available ${formatKg(load.availableKg)}). ` +
          `Remove some shipments or use another vehicle.`
      )
      return
    }

    try {
      setLoading(true)

      const payload = {
        from_location_ids: formData.from_location_ids,
        to_location_ids: formData.to_location_ids,
        crossing_location_hub_ids: canSelectCrossingHubs ? formData.crossing_location_hub_ids : [],
        vehicle_id: formData.vehicle_id,
        driver_contact: formData.driver_contact,
        shipment_ids: selectedShipmentIds,
        access_user_ids: assignToLocation ? [] : formData.access_user_ids,
        assigned_location_hub_id: assignToLocation ? assignedLocationHubId : null,
        route_id: formData.route_id || null,
      }

      console.log("[Dynamic] Creating ML Sheet with payload:", payload)

      const response = await mlSheetService.createMLSheet(payload)
      showMlSheetAutoSyncAlerts(response, { baseSuccess: 'ML Sheet created successfully' })
      navigate("/ml-sheets")
    } catch (error) {
      console.error("Error creating ML Sheet:", error)
      Alert.error(error.response?.data?.error || "Failed to create ML Sheet")
    } finally {
      setLoading(false)
    }
  }

  return (
    <DashboardLayout fullWidth fillViewport={isMobileLayout}>
                <form
                  id="add-ml-sheet-form"
                  noValidate={isMobileLayout}
                  onSubmit={handleSubmit}
                  className={`min-w-0 flex flex-col md:[&_input]:py-1.5 md:[&_select]:py-1.5 ${isMobileLayout ? "flex-1 min-h-0 overflow-hidden pb-20" : "space-y-3 sm:space-y-4"}`}
                >
                <MlPaidSettlementGate
                  mlCreationBlock={mlCreationBlock}
                  dues={dues}
                  agentDues={agentDues}
                  show={showPaidSettlementGate}
                  hardBlocked={mlHardBlocked}
                  settleOpen={settleOpen}
                  setSettleOpen={setSettleOpen}
                  onSettleSubmitted={refreshMlSettlement}
                  settlementPath={settlementPath}
                  allowSettle={!isHubAdmin}
                  collapsible
                  defaultExpanded={false}
                  className="mb-3 shrink-0"
                />
                {selectedPaidBlocked.length > 0 && (
                  <div className="text-xs sm:text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 mb-3">
                    Selected Paid shipment(s) still have unremitted collections:{' '}
                    <span className="font-semibold">
                      {selectedPaidBlocked.map((s) => s.shipment_id).join(', ')}
                    </span>
                    . Remove them or settle before creating this ML.
                  </div>
                )}
                {isMobileLayout && (
                  <div className="sticky top-0 z-30 bg-white shrink-0 mb-3 pb-2 border-b border-gray-100">
                    <div className="flex items-center gap-1 mb-2">
                      {MOBILE_STEPS.map((step, idx) => (
                        <button
                          key={step.id}
                          type="button"
                          onClick={() => handleMobileStepTabClick(idx)}
                          className={`flex-1 min-w-0 text-center py-1.5 px-0.5 rounded-lg text-[10px] font-semibold transition truncate ${
                            idx === mobileStep
                              ? "bg-orange-500 text-white shadow-sm"
                              : idx < mobileStep
                                ? "bg-orange-100 text-orange-700"
                                : "bg-gray-100 text-gray-400"
                          }`}
                        >
                          {step.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <h1 className="flex items-center gap-1.5 text-sm font-bold text-gray-700 truncate">
                        <Package className="w-4 h-4 text-buttonhoverTxt shrink-0" />
                        <span className="truncate">{MOBILE_STEPS[mobileStep].label}</span>
                      </h1>
                      <Button
                        variant="secondary"
                        type="button"
                        size="sm"
                        icon={XCircle}
                        mobileIconOnly
                        mobileLabel="Cancel"
                        onClick={() => navigate("/ml-sheets")}
                      />
                    </div>
                  </div>
                )}

                <div className="hidden md:block shrink-0">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <h1 className="text-lg sm:text-2xl font-bold text-gray-700">{t('pages.mlSheet.add')}</h1>
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                      <Button variant="primary" onClick={handleSubmit} disabled={loading || submitBlocked || isOverweight} loading={loading} className="w-full sm:w-auto">
                        {loading ? "Creating..." : "Create"}
                      </Button>
                      <Button variant="secondary" type="button" onClick={() => navigate("/ml-sheets")} className="w-full sm:w-auto">
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>

                <div className={isMobileLayout ? "contents" : "space-y-3 sm:space-y-4"}>
                      {/* Location & Date Filters */}
                  <div className={isMobileLayout ? "contents" : "grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4"}>
                    <div className={`bg-white rounded-xl shadow p-3 sm:p-4 min-w-0 ${mobileStepPanelClass(0)} md:!block`}>
                      <h3 className="font-semibold text-secondary text-sm sm:text-base mb-3 sm:mb-4">Location & Date Filters</h3>

                      <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-3">
                        <div className={`relative min-w-0 ${routeLocked || fromLocationLocked ? "opacity-60 pointer-events-none" : ""}`} ref={fromRef}>
                          <label className={ML_MOBILE_FIELD_LABEL}>
                            From Locations <span className="text-red-500 ml-1">*</span>
                          </label>
                          <div
                            onClick={() =>
                              setOpenDropdown(openDropdown === "from" ? null : "from")
                            }
                            className={mlMobileTriggerClass(openDropdown === "from")}
                          >
                            <span className={ML_MOBILE_TRIGGER_TEXT}>
                              {formData.from_location_ids.length > 0
                                ? `${formData.from_location_ids.length} selected`
                                : "Select locations"}
                            </span>
                            <span
                              className={`shrink-0 text-[10px] sm:text-sm transition ${
                                openDropdown === "from" ? "rotate-180 text-blue-500" : "text-gray-500"
                              }`}
                            >
                              ▾
                            </span>
                          </div>

                          {openDropdown === "from" && (
                            <div className={ML_MOBILE_DROPDOWN_PANEL}>
                              <input
                                type="text"
                                placeholder="Search locations..."
                                value={searchInputs.from}
                                onChange={(e) => handleSearchInput('from', e.target.value)}
                                autoFocus
                                className={ML_MOBILE_DROPDOWN_SEARCH}
                              />
                              <div className="max-h-48 overflow-y-auto">
                                {getFilteredLocations(searchInputs.from).map((loc) => {
                                  const locId = loc.id || loc.hub_id
                                  return (
                                    <label
                                      key={locId}
                                      className={ML_MOBILE_DROPDOWN_ITEM}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isLocationSelected(formData.from_location_ids, locId)}
                                        onChange={() => handleLocationToggle("from", locId)}
                                        className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 mt-0.5"
                                      />
                                      <span className={ML_MOBILE_DROPDOWN_ITEM_TEXT}>
                                        {locationOptionLabel(loc)}
                                      </span>
                                    </label>
                                  )
                                })}
                              </div>
                            </div>
                          )}
                          <div className="mt-1 min-w-0">
                          <MlLocationChips
                            compact
                            locations={locations}
                            selectedIds={formData.from_location_ids}
                            useLrLabels={useLrLocationLabels}
                            onRemove={(routeLocked || fromLocationLocked) ? undefined : (id) => handleLocationToggle("from", id)}
                          />
                          </div>
                        </div>

                        <div className={`relative min-w-0 ${routeLocked ? "opacity-60 pointer-events-none" : ""}`} ref={toRef}>
                          <label className={ML_MOBILE_FIELD_LABEL}>
                            To Locations <span className="text-red-500 ml-1">*</span>
                          </label>

                          <div
                            onClick={() =>
                              setOpenDropdown(openDropdown === "to" ? null : "to")
                            }
                            className={mlMobileTriggerClass(openDropdown === "to")}
                          >
                            <span className={ML_MOBILE_TRIGGER_TEXT}>
                              {formData.to_location_ids.length > 0
                                ? `${formData.to_location_ids.length} selected`
                                : "Select locations"}
                            </span>

                            <span
                              className={`shrink-0 text-[10px] sm:text-sm transition ${
                                openDropdown === "to" ? "rotate-180 text-blue-500" : "text-gray-500"
                              }`}
                            >
                              ▾
                            </span>
                          </div>

                          {openDropdown === "to" && (
                            <div className={ML_MOBILE_DROPDOWN_PANEL}>
                              <input
                                type="text"
                                placeholder="Search locations..."
                                value={searchInputs.to}
                                onChange={(e) => handleSearchInput('to', e.target.value)}
                                autoFocus
                                className={ML_MOBILE_DROPDOWN_SEARCH}
                              />
                              <div className="max-h-48 overflow-y-auto">
                                {Array.isArray(locations) && getFilteredToLocations(searchInputs.to).length > 0 ? (
                                  getFilteredToLocations(searchInputs.to).map((loc) => {
                                    const locId = loc.id || loc.hub_id
                                    return (
                                      <label
                                        key={locId}
                                        className={ML_MOBILE_DROPDOWN_ITEM}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isLocationSelected(formData.to_location_ids, locId)}
                                          onChange={() => handleLocationToggle("to", locId)}
                                          className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 mt-0.5"
                                        />
                                        <span className={ML_MOBILE_DROPDOWN_ITEM_TEXT}>
                                          {locationOptionLabel(loc)}
                                        </span>
                                      </label>
                                    )
                                  })
                                ) : (
                                  <p className="text-[10px] sm:text-sm text-gray-500 p-2">
                                    {formData.from_location_ids.length === 0
                                      ? "Select From location first"
                                      : "No matching branch destinations for this From dispatch type"}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="mt-1 min-w-0">
                          <MlLocationChips
                            compact
                            locations={locations}
                            selectedIds={formData.to_location_ids}
                            useLrLabels={useLrLocationLabels}
                            onRemove={routeLocked ? undefined : (id) => handleLocationToggle("to", id)}
                          />
                          </div>
                        </div>

                        <div className={`relative min-w-0 ${canSelectCrossingHubs ? "" : "col-span-2"}`} ref={routeRef}>
                          <label className={ML_MOBILE_FIELD_LABEL}>Route (optional)</label>
                          <div
                            onClick={() =>
                              setOpenDropdown(openDropdown === "route" ? null : "route")
                            }
                            className={mlMobileTriggerClass(openDropdown === "route")}
                          >
                            <span className={`${ML_MOBILE_TRIGGER_TEXT} whitespace-normal break-words line-clamp-2`}>
                              {formData.route_id
                                ? formatRouteLabel(routes.find((r) => String(r.id) === String(formData.route_id))) || "Custom — select locations manually"
                                : "Custom — select locations manually"}
                            </span>
                            <span
                              className={`shrink-0 text-[10px] sm:text-sm transition ${
                                openDropdown === "route" ? "rotate-180 text-blue-500" : "text-gray-500"
                              }`}
                            >
                              ▾
                            </span>
                          </div>
                          {openDropdown === "route" && (
                            <div className={ML_MOBILE_DROPDOWN_PANEL}>
                              <input
                                type="text"
                                placeholder="Search routes..."
                                value={searchInputs.route}
                                onChange={(e) => handleSearchInput("route", e.target.value)}
                                autoFocus
                                className={ML_MOBILE_DROPDOWN_SEARCH}
                              />
                              <div className="max-h-48 overflow-y-auto">
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleRouteSelect({ target: { value: "" } })
                                    setOpenDropdown(null)
                                    setSearchInputs((prev) => ({ ...prev, route: "" }))
                                  }}
                                  className={`${ML_MOBILE_DROPDOWN_ITEM} w-full text-left ${
                                    !formData.route_id ? "bg-blue-50" : ""
                                  }`}
                                >
                                  <span className={`${ML_MOBILE_DROPDOWN_ITEM_TEXT} ${!formData.route_id ? "text-blue-700" : ""}`}>
                                    Custom — select locations manually
                                  </span>
                                </button>
                                {getFilteredRoutes(searchInputs.route).length > 0 ? (
                                  getFilteredRoutes(searchInputs.route).map((route) => {
                                    const isSelected = String(route.id) === String(formData.route_id)
                                    return (
                                      <button
                                        key={route.id}
                                        type="button"
                                        onClick={() => {
                                          handleRouteSelect({ target: { value: route.id } })
                                          setOpenDropdown(null)
                                          setSearchInputs((prev) => ({ ...prev, route: "" }))
                                        }}
                                        className={`${ML_MOBILE_DROPDOWN_ITEM} w-full text-left ${
                                          isSelected ? "bg-blue-50" : ""
                                        }`}
                                      >
                                        <span className={`${ML_MOBILE_DROPDOWN_ITEM_TEXT} whitespace-normal break-words ${
                                          isSelected ? "text-blue-700" : ""
                                        }`}>
                                          {formatRouteLabel(route)}
                                        </span>
                                      </button>
                                    )
                                  })
                                ) : (
                                  <p className="text-[10px] sm:text-sm text-gray-500 p-2">No matching routes</p>
                                )}
                              </div>
                            </div>
                          )}
                          {routeLocked ? (
                            <p className="text-[10px] sm:text-xs text-blue-600 mt-1 break-words">
                              Locations are set by the selected route. Choose “Custom” to edit manually.
                            </p>
                          ) : (
                            formData.route_id && (
                              <p className="text-[10px] sm:text-xs text-green-600 mt-1 break-words">
                                Matched route auto-selected for these locations.
                              </p>
                            )
                          )}
                        </div>

                        {canSelectCrossingHubs && (
                          <div className="relative min-w-0" ref={crossingRef}>
                            <label className={ML_MOBILE_FIELD_LABEL}>
                              Crossing Location Hubs
                            </label>
                            <div
                              onClick={() =>
                                setOpenDropdown(openDropdown === "crossing" ? null : "crossing")
                              }
                              className={mlMobileTriggerClass(openDropdown === "crossing")}
                            >
                              <span className={ML_MOBILE_TRIGGER_TEXT}>
                                {formData.crossing_location_hub_ids.length > 0
                                  ? `${formData.crossing_location_hub_ids.length} selected`
                                  : "Select location hubs"}
                              </span>
                              <span
                                className={`shrink-0 text-[10px] sm:text-sm transition ${
                                  openDropdown === "crossing" ? "rotate-180 text-blue-500" : "text-gray-500"
                                }`}
                              >
                                ▾
                              </span>
                            </div>

                            {openDropdown === "crossing" && (
                              <div className={ML_MOBILE_DROPDOWN_PANEL}>
                                <input
                                  type="text"
                                  placeholder="Search location hubs..."
                                  value={searchInputs.crossing}
                                  onChange={(e) => handleSearchInput("crossing", e.target.value)}
                                  autoFocus
                                  className={ML_MOBILE_DROPDOWN_SEARCH}
                                />
                                <div className="max-h-48 overflow-y-auto">
                                  {getFilteredLocations(searchInputs.crossing).map((loc) => {
                                    const locId = loc.id || loc.hub_id
                                    return (
                                      <label
                                        key={locId}
                                        className={ML_MOBILE_DROPDOWN_ITEM}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isLocationSelected(formData.crossing_location_hub_ids, locId)}
                                          onChange={() => handleCrossingHubToggle(locId)}
                                          className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 mt-0.5"
                                        />
                                        <span className={ML_MOBILE_DROPDOWN_ITEM_TEXT}>
                                          {locationOptionLabel(loc)}
                                        </span>
                                      </label>
                                    )
                                  })}
                                </div>
                              </div>
                            )}
                            <div className="mt-1 min-w-0">
                            <MlLocationChips
                              compact
                              locations={locations}
                              selectedIds={formData.crossing_location_hub_ids}
                              useLrLabels={useLrLocationLabels}
                              onRemove={handleCrossingHubToggle}
                            />
                            </div>
                            <p className="text-[10px] sm:text-xs text-gray-500 mt-1 break-words">
                              Agents at these hubs can create a crossing from QR.
                            </p>
                          </div>
                        )}

                        <div className="min-w-0">
                          <label className={ML_MOBILE_FIELD_LABEL}>From Date</label>
                          <input
                            type="date"
                            name="from_date"
                            value={formData.from_date}
                            onChange={handleDateChange}
                            className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={ML_MOBILE_FIELD_LABEL}>To Date</label>
                          <input
                            type="date"
                            name="to_date"
                            value={formData.to_date}
                            onChange={handleDateChange}
                            className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                          />
                        </div>

                      </div>
                    </div>
                    <div className={isMobileLayout ? "contents" : "grid grid-cols-1 gap-3 sm:gap-4 min-w-0"}>
                      {/* Vehicle & Driver Info */}
                      <div className={`bg-white rounded-xl shadow p-3 sm:p-4 min-w-0 ${mobileStepPanelClass(1)} md:!block`}>
                        <h3 className="font-semibold text-secondary text-sm sm:text-base mb-3 sm:mb-4">Vehicle & Driver Information</h3>
                        <div className="space-y-3 sm:space-y-4">
                        <div className="grid grid-cols-2 gap-2 sm:gap-3">
                          <div className="relative min-w-0" ref={vehicleRef}>
                            <label className={ML_MOBILE_FIELD_LABEL}>Vehicle <span className="text-red-500 ml-1">*</span></label>
                            <div
                              onClick={() =>
                                setOpenDropdown(openDropdown === "vehicle" ? null : "vehicle")
                              }
                              className={mlMobileTriggerClass(openDropdown === "vehicle")}
                            >
                              <span className={ML_MOBILE_TRIGGER_TEXT}>
                                {formData.vehicle_id
                                  ? (() => {
                                    const vehicle = vehicles.find(v => (v.id || v.vehicle_id) === formData.vehicle_id)
                                    return vehicle ? formatVehicleLabel(vehicle) : "Select a vehicle"
                                  })()
                                  : "Select a vehicle"}
                              </span>
                              <span
                                className={`shrink-0 text-[10px] sm:text-sm transition ${
                                  openDropdown === "vehicle" ? "rotate-180 text-blue-500" : "text-gray-500"
                                }`}
                              >
                                ▾
                              </span>
                            </div>

                            {openDropdown === "vehicle" && (
                              <div className={ML_MOBILE_DROPDOWN_PANEL}>
                                <input
                                  type="text"
                                  placeholder="Search vehicles..."
                                  value={searchInputs.vehicle}
                                  onChange={(e) => handleSearchInput('vehicle', e.target.value)}
                                  autoFocus
                                  className={ML_MOBILE_DROPDOWN_SEARCH}
                                />
                                <div className="max-h-48 overflow-y-auto">
                                  {getFilteredVehicles(searchInputs.vehicle).length > 0 ? (
                                    getFilteredVehicles(searchInputs.vehicle).map((v) => {
                                      const vehicleId = v.id || v.vehicle_id
                                      return (
                                        <div
                                          key={vehicleId}
                                          onClick={() => {
                                            handleVehicleChange({target: {value: vehicleId}})
                                            setOpenDropdown(null)
                                            setSearchInputs(prev => ({...prev, vehicle: ""}))
                                          }}
                                          className={`${ML_MOBILE_DROPDOWN_ITEM} border-b border-gray-100 last:border-b-0`}
                                        >
                                          <span className={`${ML_MOBILE_DROPDOWN_ITEM_TEXT} font-medium text-gray-800`}>
                                            {formatVehicleLabel(v)}
                                          </span>
                                        </div>
                                      )
                                    })
                                  ) : (
                                    <p className="text-[10px] sm:text-sm text-gray-500 p-2">No vehicles found</p>
                                  )}
                                </div>
                                {canAddVehicle && (
                                <button
                                  type="button"
                                  onClick={() => openVehicleModal(searchInputs.vehicle)}
                                  className="w-full flex items-center justify-center gap-2 px-2 py-1.5 mt-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 text-[10px] sm:text-sm font-medium"
                                >
                                  <Plus size={14} />
                                  Add New Vehicle
                                </button>
                                )}
                              </div>
                            )}
                            <CreateVehicleWithOwnerModal
                              open={showVehicleModal}
                              onClose={() => setShowVehicleModal(false)}
                              onSuccess={handleVehicleCreated}
                              initialVehicleNumber={pendingVehicleSearch}
                              companyAccess={companyAccess}
                            />
                            {(() => {
                              const sel = vehicles.find(v => (v.id || v.vehicle_id) === formData.vehicle_id)
                              return sel?.owner_name ? (
                                <p className="text-xs text-gray-600 mt-1">
                                  Vehicle owner: <span className="font-medium">{sel.owner_name}</span>
                                </p>
                              ) : null
                            })()}
                          </div>
                          <div className="min-w-0">
                            <label className={ML_MOBILE_FIELD_LABEL}>Driver Contact</label>
                            <input
                              type="tel"
                              inputMode="numeric"
                              maxLength={10}
                              value={formData.driver_contact}
                              onChange={handleDriverContactChange}
                              placeholder="10-digit mobile number"
                              className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                            />
                          </div>
                        </div>
                          <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                          <div className="relative w-full sm:w-56 lg:w-64 shrink-0" ref={accessRef}>
                            <MlAssignToLocationBlock
                              eligibleHubs={locationAssignHubs}
                              assignToLocation={assignToLocation}
                              setAssignToLocation={setAssignToLocation}
                              assignedLocationHubId={assignedLocationHubId}
                              setAssignedLocationHubId={setAssignedLocationHubId}
                            />
                            {!assignToLocation && (
                            <>
                            <label className={ML_MOBILE_FIELD_LABEL}>
                              ML Access Agents{' '}
                              {accessUsersRequired ? (
                                <span className="text-red-500">*</span>
                              ) : (
                                <span className="text-gray-400 font-normal">(optional)</span>
                              )}
                            </label>
                            <div
                              onClick={() =>
                                setOpenDropdown(openDropdown === "access" ? null : "access")
                              }
                              className={`w-full ${mlMobileTriggerClass(openDropdown === "access")}`}
                            >
                              <span className={ML_MOBILE_TRIGGER_TEXT}>
                                {formData.access_user_ids.length > 0
                                  ? `${formData.access_user_ids.length} selected`
                                  : "Select users"}
                              </span>
                              <span
                                className={`shrink-0 text-[10px] sm:text-sm transition ${
                                  openDropdown === "access" ? "rotate-180 text-blue-500" : "text-gray-500"
                                }`}
                              >
                                ▾
                              </span>
                            </div>
                            {openDropdown === "access" && (
                              <div className={ML_MOBILE_DROPDOWN_PANEL}>
                                <input
                                  type="text"
                                  placeholder="Search by name, location, contact..."
                                  value={searchInputs.access}
                                  onChange={(e) => handleSearchInput('access', e.target.value)}
                                  autoFocus
                                  className={ML_MOBILE_DROPDOWN_SEARCH}
                                />
                                <div className="max-h-56 overflow-y-auto">
                                  {Array.isArray(agents) && accessAgentGroups.all.length > 0 ? (
                                    <>
                                      {accessAgentGroups.hasToFilter && accessAgentGroups.matched.length > 0 && (
                                        <>
                                          <p className="text-[11px] font-semibold text-indigo-700 px-2 py-1 sticky top-0 bg-white">
                                            Agents at destination
                                          </p>
                                          {accessAgentGroups.matched.map(renderAccessAgentOption)}
                                        </>
                                      )}
                                      {accessAgentGroups.hasToFilter && accessAgentGroups.others.length > 0 && (
                                        <>
                                          <p className="text-[11px] font-semibold text-gray-500 px-2 py-1 mt-1 border-t border-gray-100 sticky top-0 bg-white">
                                            Other agents
                                          </p>
                                          {accessAgentGroups.others.map(renderAccessAgentOption)}
                                        </>
                                      )}
                                      {!accessAgentGroups.hasToFilter &&
                                        accessAgentGroups.all.map(renderAccessAgentOption)}
                                    </>
                                  ) : (
                                    <p className="text-sm text-gray-500 p-2">
                                      {!agents.length
                                        ? 'No users available'
                                        : searchInputs.access.trim()
                                        ? 'No users match your search'
                                        : 'No users available'}
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                            </>
                            )}
                          </div>
                          {!assignToLocation && (
                          <div className="min-w-0 flex-1 sm:pt-6">
                            <MlAccessAgentChips
                              agents={agents}
                              selectedIds={formData.access_user_ids}
                              onRemove={handleAccessUserChange}
                            />
                          </div>
                          )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>


                  {/* Shipments Selection */}
                  <div className={`bg-white rounded-xl shadow p-3 sm:p-4 min-w-0 max-w-full ${mobileStepPanelClass(2)} md:!block`}>
                    <div className="flex flex-wrap items-center justify-between mb-3 sm:mb-4 gap-2 sm:gap-3 shrink-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 min-w-0">
                        <h3 className="font-semibold text-secondary text-sm sm:text-base whitespace-nowrap">
                          Select Shipments ({selectedShipmentIds.length}/{filteredShipments.length})
                        </h3>
                        {searchingShipments && <span className="text-xs text-blue-600">Searching...</span>}

                        {isCargoMode && load.hasCapacity && selectedShipmentIds.length > 0 && (
                          <span
                            title={
                              `New ${formatKg(load.newWeight)} + already today ${formatKg(load.alreadyLoadedKg)} ` +
                              `= ${formatKg(load.totalWeight)} / capacity ${formatKg(load.capacityKg)} ` +
                              `(available ${formatKg(load.availableKg)})` +
                              (isOverweight ? ` — over by ${formatKg(load.excessKg)}` : "")
                            }
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] sm:text-xs font-medium ${
                              isOverweight
                                ? "border-red-200 bg-red-50 text-red-700"
                                : "border-green-200 bg-green-50 text-green-700"
                            }`}
                          >
                            {isOverweight ? (
                              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                            ) : (
                              <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                            )}
                            <span className="whitespace-nowrap">
                              <span className="font-semibold">
                                {isOverweight ? "Overweight" : "Within capacity"}
                              </span>
                              <span className="hidden sm:inline opacity-70">
                                {" · "}{formatKg(load.newWeight)}+{formatKg(load.alreadyLoadedKg)}/
                                {formatKg(load.capacityKg)}
                                {isOverweight ? ` (+${formatKg(load.excessKg)})` : ""}
                              </span>
                            </span>
                            {isOverweight && load.extraShipmentIds.length > 0 && (
                              <button
                                type="button"
                                onClick={handleRemoveExtraShipments}
                                className="ml-0.5 rounded-md bg-red-600 px-1.5 py-0.5 text-[10px] sm:text-[11px] font-semibold text-white hover:bg-red-700 whitespace-nowrap"
                              >
                                Remove {load.extraShipmentIds.length}
                              </button>
                            )}
                          </span>
                        )}
                      </div>
                      {filteredShipments.length > 0 && (
                        <>
                          <label className="flex xl:hidden items-center gap-2.5 cursor-pointer py-1 shrink-0">
                            <input
                              type="checkbox"
                              checked={selectAllChecked}
                              onChange={handleSelectAll}
                              className="w-5 h-5 flex-shrink-0 accent-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700">
                              {selectAllChecked ? "Deselect All" : "Select All"}
                            </span>
                          </label>
                          <button
                            type="button"
                            onClick={handleSelectAll}
                            className="hidden xl:inline-flex w-auto px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 font-semibold text-sm shrink-0"
                          >
                            {selectAllChecked ? "Deselect All" : "Select All"}
                          </button>
                        </>
                      )}
                    </div>

                    <div className={`min-w-0 max-w-full ${isMobileLayout ? "flex-1 min-h-0 overflow-y-auto overscroll-contain" : "overflow-x-auto"}`}>
                      {searchingShipments ? (
                        <Loader fullScreen={false} size="small" message="Searching for shipments..." />
                      ) : (
                        <MLSheetShipmentRecordList
                          shipments={filteredShipments}
                          selectedShipmentIds={selectedShipmentIds}
                          onToggle={handleSelectShipment}
                          onSelectAll={handleSelectAll}
                          selectAllChecked={selectAllChecked}
                          isShipmentDisabled={shipmentDisabledForMl}
                          getStatusColor={(status) => {
                            const statusMap = {
                              booked: "bg-blue-100 text-blue-800",
                              dispatched: "bg-purple-100 text-purple-800",
                              received: "bg-indigo-100 text-indigo-800",
                              delivered: "bg-green-100 text-green-800",
                              cancelled: "bg-red-100 text-red-800",
                            }
                            return statusMap[status?.toLowerCase()] || "bg-gray-100 text-gray-800"
                          }}
                          emptyMessage={
                            formData.from_location_ids.length > 0 ||
                            formData.to_location_ids.length > 0 ||
                            formData.from_date ||
                            formData.to_date
                              ? "No booked shipments found for selected filters"
                              : "Please select location or date filters to search for shipments"
                          }
                        />
                      )}
                    </div>
                  </div>
                </div>
                </form>

                {isMobileLayout && (
                  <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between gap-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] md:hidden">
                    {mobileStep > 0 ? (
                      <Button variant="secondary" type="button" icon={ChevronLeft} mobileIconOnly mobileLabel="Back" onClick={goToPrevMobileStep} />
                    ) : (
                      <div className="w-10" />
                    )}
                    <Button
                      variant="primary"
                      type="button"
                      icon={mobileStep < MOBILE_STEPS.length - 1 ? ChevronRight : undefined}
                      iconPosition="right"
                      onClick={handleMobilePrimaryAction}
                      loading={loading && mobileStep >= MOBILE_STEPS.length - 1}
                      disabled={loading || submitBlocked || (mobileStep >= MOBILE_STEPS.length - 1 && isOverweight)}
                    >
                      {mobileStep < MOBILE_STEPS.length - 1
                        ? "Next"
                        : loading
                          ? "Creating..."
                          : "Create"}
                    </Button>
                  </div>
                )}
    </DashboardLayout>
  )
}

export default AddMLSheet;
